/** Preekie Meetings: pure helpers and the approved words (mockups
 * "Preekie Meetings" revision 3, approved by Prabash and Krissie). No DOM
 * here -- tested with Node (web/console/test/meetings.test.ts). The API is
 * docs/meetings-api.md (session A). */

// Live camera video and screen share are in v1 (Prabash, 2026-10-03, as
// in the approved screens). Recording stays audio only: the server's
// capture pipeline takes audio, whatever the room sends.
export const VIDEO_AND_SHARE = true;

export const MEETINGS_COPY = {
  startInPerson: "Start in-person meeting",
  newMeeting: "New meeting",
  // What happens to a meeting's data, in the Privacy Policy's own terms (v1.1, section 7): audio goes once it's transcribed;
  // transcripts, notes (drafts too) and consent records stay while the account is active. Prabash, 22 Oct 2026.
  footnote: "Audio is deleted once it's transcribed. Transcripts and notes are kept as set out in our",
  privacyPolicy: "Privacy Policy",
  clientRecordLead: "Recording from here files the notes against this client.",
  inviteExplainer: "Each person gets an email with a Join button and a calendar invite. Guests don't need a Preekie account.",
  sendInvites: "Send invites",
  copyLinkInstead: "Copy link instead",
  cancel: "Cancel",
  // Honest until the server sends invite emails (decision 0046 point 8,
  // waiting on SES): nothing is emailed yet, so the host copies each link.
  emailsNotLive: "Invite emails aren't switched on yet. Copy each person's link and send it to them yourself.",
  yourName: "Your name",
  iAgree: "I agree",
  join: "Join",
  waitingForHost: "You're in the lobby. The host will let you in shortly.",
  preekieTakingNotes: "Preekie · taking notes",
  preekieRecordingLabel: "Preekie (recording)",
  notRecording: "Not recording",
  inTheMeeting: "In the meeting",
  waitingInLobby: "Waiting in the lobby",
  admit: "Admit",
  mute: "Mute",
  unmute: "Unmute",
  camera: "Camera",
  shareScreen: "Share screen",
  startRecording: "Start recording",
  stopRecording: "Stop recording",
  leave: "Leave",
  endMeeting: "End meeting for everyone",
  readyToTakeNotes: "Preekie is ready to take notes",
  beforeYouStart: "Before you start",
  showOrRead: "Show or read this to everyone in the room",
  everyoneAgreed: "I've told everyone and they've agreed.",
  start: "Start",
  decisions: "Decisions",
  actions: "Actions",
  figuresAndDeadlines: "Figures and deadlines mentioned",
  openQuestions: "Open questions",
  handOver: "Hand over",
  handOverTo: "Hand over to",
  note: "Note",
  approveAndHandOver: "Approve and hand over",
  editSummary: "Edit summary",
  transcriptExcerpt: "Transcript excerpt",
  openFullTranscript: "Open full transcript",
  preparing: "Preekie is preparing the notes. They'll appear here once they're ready.",
} as const;

/** The one-line recording notice: a copy of services/meetings/meetings.py's
 * RECORDING_NOTICE, which the guest lobby gets from the API. The host's
 * in-person screen shows it without an API call;
 * services/console_api/test_meetings_screens.py keeps the two the same. */
export const RECORDING_NOTICE =
  "This meeting will be recorded so Preekie can prepare notes. The recording is deleted as soon as the transcript exists.";

/** "The audio was deleted at 8:20 pm, once the transcript was made." */
export function audioDeletedLine(iso: string, timeZone?: string): string {
  return `The audio was deleted at ${formatTime(iso, timeZone)}, once the transcript was made.`;
}

// ---- Times: always am/pm, in the viewer's own time zone ----

function parts(iso: string, timeZone: string | undefined, options: Intl.DateTimeFormatOptions) {
  const fmt = new Intl.DateTimeFormat("en-AU", { ...options, timeZone });
  return Object.fromEntries(fmt.formatToParts(new Date(iso)).map((p) => [p.type, p.value])) as Record<string, string>;
}

/** "8:30 pm" -- lower-case am/pm, no leading zero on the hour. */
export function formatTime(iso: string, timeZone?: string): string {
  const p = parts(iso, timeZone, { hour: "numeric", minute: "2-digit", hour12: true });
  return `${p.hour}:${p.minute} ${(p.dayPeriod ?? "").toLowerCase()}`;
}

/** "14/10/2026, 8:30 pm" -- the brand's table date, then the time. */
export function formatTableWhen(iso: string, timeZone?: string): string {
  const p = parts(iso, timeZone, { day: "2-digit", month: "2-digit", year: "numeric" });
  return `${p.day}/${p.month}/${p.year}, ${formatTime(iso, timeZone)}`;
}

/** "Tue 14 Oct, 8:30 pm" -- for prose and the meeting details line. */
export function formatWhen(iso: string, timeZone?: string): string {
  const p = parts(iso, timeZone, { weekday: "short", day: "numeric", month: "short" });
  return `${p.weekday} ${p.day} ${p.month}, ${formatTime(iso, timeZone)}`;
}

/** The viewer's own IANA time zone, e.g. "Australia/Sydney". */
export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Minutes after midnight as "9:15 am". */
export function clockLabel(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = String(minutes % 60).padStart(2, "0");
  return `${h % 12 === 0 ? 12 : h % 12}:${m} ${h < 12 ? "am" : "pm"}`;
}

/** "45 min", "1 hr", "1 hr 30 min". */
export function lengthLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

/** "03:12", or "1:03:12" past an hour -- elapsed recording time. */
export function elapsed(sinceIso: string, now: Date): string {
  const total = Math.max(0, Math.floor((now.getTime() - new Date(sinceIso).getTime()) / 1000));
  const h = Math.floor(total / 3600);
  const mm = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function recordingLabel(recordingStatus: string, startedAt: string | null, now: Date): string {
  return recordingStatus === "recording" && startedAt ? `Recording · ${elapsed(startedAt, now)}` : MEETINGS_COPY.notRecording;
}

/** A local date ("2026-10-14") and time ("09:30") in `timeZone`, as ISO
 * 8601 with that zone's offset at that moment: "2026-10-14T09:30:00+11:00". */
export function localToIso(date: string, time: string, timeZone: string): string {
  const [y, mo, d] = date.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  // Guess the instant as UTC, then correct by the zone's offset there (twice,
  // so a guess across a daylight-saving change still lands right).
  let instant = Date.UTC(y!, mo! - 1, d!, h!, mi!);
  for (let i = 0; i < 2; i++) instant = Date.UTC(y!, mo! - 1, d!, h!, mi!) - offsetMinutes(instant, timeZone) * 60000;
  const off = offsetMinutes(instant, timeZone);
  const sign = off >= 0 ? "+" : "-";
  const abs = Math.abs(off);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date}T${pad(h!)}:${pad(mi!)}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

function offsetMinutes(instant: number, timeZone: string): number {
  const p = parts(new Date(instant).toISOString(), timeZone, {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  });
  const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour) % 24, Number(p.minute));
  return Math.round((asUtc - instant) / 60000);
}

// ---- Statuses (the brand's six; Meetings uses four) ----

export type MeetingStatus = "Requested" | "Awaiting review" | "Approved" | "Exception";

/** The brand status pair for each display status. Colour never alone: the
 * label is always shown with it. */
export function statusStyle(status: string): "neutral" | "review" | "approved" | "exception" {
  switch (status) {
    case "Awaiting review":
      return "review";
    case "Approved":
      return "approved";
    case "Exception":
      return "exception";
    default:
      return "neutral"; // Requested
  }
}

// ---- New meeting ----

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MAX_GUESTS = 20; // the API's limit

/** Splits typed text into email chips: valid, de-duplicated (case-insensitive). */
export function parseEmails(text: string, existing: readonly string[] = []): { added: string[]; invalid: string[] } {
  const seen = new Set(existing.map((e) => e.toLowerCase()));
  const added: string[] = [];
  const invalid: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const email = raw.trim();
    if (!email) continue;
    if (!EMAIL.test(email)) invalid.push(email);
    else if (!seen.has(email.toLowerCase())) {
      seen.add(email.toLowerCase());
      added.push(email);
    }
  }
  return { added, invalid };
}

export interface NewMeetingForm {
  title: string;
  clientSlug: string;
  date: string; // yyyy-mm-dd, the viewer's local date
  time: string; // hh:mm, the viewer's local time
  lengthMinutes: number;
  emails: string[];
  mode: "online" | "in_person";
}

/** What's wrong with the form, in plain words, or [] when it can be sent. */
export function formProblems(form: NewMeetingForm): string[] {
  const problems: string[] = [];
  if (!form.title.trim()) problems.push("Add a title.");
  if (form.title.trim().length > 120) problems.push("Keep the title under 120 characters.");
  if (!form.clientSlug) problems.push("Choose a client.");
  if (form.mode === "online" && (!form.date || !form.time)) problems.push("Choose a date and time.");
  if (!(form.lengthMinutes >= 5 && form.lengthMinutes <= 480)) problems.push("Choose a length between 5 minutes and 8 hours.");
  if (form.emails.length > MAX_GUESTS) problems.push(`Invite at most ${MAX_GUESTS} people.`);
  return problems;
}

export function createPayload(form: NewMeetingForm, timeZone: string, now: Date = new Date()): Record<string, unknown> {
  const start = form.mode === "in_person" && (!form.date || !form.time)
    ? now.toISOString().replace(/\.\d{3}Z$/, "+00:00")
    : localToIso(form.date, form.time, timeZone);
  return {
    title: form.title.trim(),
    client_slug: form.clientSlug,
    scheduled_start: start,
    length_minutes: form.lengthMinutes,
    invited_guest_emails: form.emails,
    meeting_mode: form.mode,
  };
}

// ---- An action's due date, as the notes show it ----

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** How the notes show an action's deadline: the API's `due_display` (#190),
 * e.g. "tomorrow (13 Nov)", with the date part pulled out so it can be set
 * as a figure. `words` is shown as plain text; `date` (when present) in DM
 * Mono, in brackets after the words or on its own. Records from before #190
 * have no `due_display`: then the date ("Due 16 Oct") or the words alone. */
export function actionDue(action: { due_date: string | null; due_text?: string | null; due_display?: string | null }): {
  words: string;
  date: string | null;
  bracketed: boolean;
} {
  const display = action.due_display?.trim();
  if (display) {
    const dateOnly = display.match(LIST_DATE);
    if (dateOnly) return { words: "Due", date: display, bracketed: false };
    const withDate = display.match(/^(.*\S)\s*\((\d{1,2} [A-Z][a-z]{2})\)$/);
    if (withDate) return { words: `Due ${withDate[1]}`, date: withDate[2]!, bracketed: true };
    return { words: `Due ${display}`, date: null, bracketed: false };
  }
  if (action.due_date) {
    const [, m, d] = action.due_date.split("-").map(Number);
    return { words: "Due", date: `${d} ${SHORT_MONTHS[m! - 1]}`, bracketed: false };
  }
  const words = action.due_text?.trim();
  if (words) return { words: `Due ${words}`, date: null, bracketed: false };
  return { words: "No due date", date: null, bracketed: false };
}

const LIST_DATE = /^\d{1,2} [A-Z][a-z]{2}$/;

// ---- Reviewing the notes (Meetings launch checklist, "Show the new fields on the review screen") ----

/** Shown in view mode, edit mode and next to Approve (Prabash's wording, 2026-10-06). */
export const AI_DRAFT_NOTICE =
  "AI draft. Prepared by Preekie's AI from the meeting recording. It can miss or misread details. Check each item against the transcript before you approve.";

const UNVERIFIED_REASONS: Record<string, string> = {
  quote_not_in_transcript: "The quote isn't in the transcript.",
  figure_not_in_transcript: "A figure in this item isn't in the transcript.",
  quote_does_not_support_item: "The quote is about something else.",
  date_conflicts_with_weekday: "The date doesn't match the day of the week that was said.",
};

export type ItemCheck =
  | { kind: "verified"; quote: string | null }
  | { kind: "unverified"; label: "No source found"; reason: string; quote: string | null }
  | { kind: "edited"; label: "Edited"; note: string }
  | { kind: "unchecked" };

/** What the reviewer sees under an item. An unverified item is never hidden:
 * it's flagged "No source found" with the reason in plain words. */
export function itemCheck(item: {
  verified: boolean | null;
  evidence?: string | null;
  unverified_reason?: string | null;
  edited_by_person?: boolean;
}): ItemCheck {
  const quote = item.evidence?.trim() || null;
  if (item.verified === true) return { kind: "verified", quote };
  if (item.verified === false) {
    const reason = UNVERIFIED_REASONS[item.unverified_reason ?? ""] ?? "Preekie couldn't match it to the transcript.";
    return { kind: "unverified", label: "No source found", reason, quote };
  }
  if (item.edited_by_person) {
    return { kind: "edited", label: "Edited", note: "Written or changed by a reviewer, so it isn't checked against the transcript." };
  }
  return { kind: "unchecked" };
}

/** "Worked out from “tomorrow”: 13 Nov" -- a date the meeting never said, to check. */
export function inferredDateLine(d: { resolved: string; said: string }): { words: string; date: string } {
  const [, m, day] = d.resolved.slice(0, 10).split("-").map(Number);
  return { words: `Worked out from “${d.said}”:`, date: `${day} ${SHORT_MONTHS[m! - 1]}` };
}

/** The Approve card's counts, in plain words. */
export function reviewCounts(s: { unverified_count?: number; date_inferred_count?: number; figures_not_in_notes?: unknown[] }): {
  unverified: string;
  inferred: string | null;
  figures: string;
} {
  const n = s.unverified_count ?? 0;
  const d = s.date_inferred_count ?? 0;
  const f = s.figures_not_in_notes?.length ?? 0;
  return {
    unverified: n === 0 ? "Every item has a source in the transcript." : `${n} ${n === 1 ? "item has" : "items have"} no source found.`,
    inferred: d === 0 ? null : `${d} ${d === 1 ? "date was" : "dates were"} worked out from the words said. Check ${d === 1 ? "it" : "them"}.`,
    figures: `Figures not in the notes (${f})`,
  };
}

/** "Speaker 2 · turn 42": people count turns from 1; `turn` is a 0-based index. */
export function figureSource(f: { turn: number; speaker: string | null }): { speaker: string; turn: string } {
  return { speaker: f.speaker ?? "Unknown speaker", turn: `turn ${f.turn + 1}` };
}

// ---- Who may do what (the server enforces every rule; this decides what's shown) ----

/** Approve or edit the notes: the meeting's host or an account admin
 * (Prabash, 2026-10-03), and only while they're Awaiting review. */
export function canReview(isHost: boolean, isAccountAdmin: boolean, status: string): boolean {
  return (isHost || isAccountAdmin) && status === "Awaiting review";
}

/** Admit guests, start or stop recording, end the meeting: the host only. */
export function canRunMeeting(isHost: boolean): boolean {
  return isHost;
}

// ---- Who's on each video tile ----

/** A Chime attendee's name: from the API's participant list when it
 * carries the attendee's opaque `external_user_id` ("member:<id>" or
 * "guest:<invite_id>"), otherwise just what kind of person they are.
 * Never an email -- neither side has one. */
export function attendeeName(
  externalUserId: string,
  participants: ReadonlyArray<{ display_name: string; kind: string; external_user_id?: string }>
): string {
  const known = participants.find((p) => p.external_user_id && p.external_user_id === externalUserId);
  if (known) return known.display_name;
  return externalUserId.startsWith("guest:") ? "Guest" : "Member";
}

// ---- Guest lobby ----

/** Join is enabled only once the guest has ticked "I agree" and given a
 * name (the consent record's name, 1-60 characters). */
export function canJoin(consent: boolean, name: string): boolean {
  const n = name.trim();
  return consent && n.length >= 1 && n.length <= 60;
}

// ---- Camera and microphone ----

/** What went wrong, and what to do, for a getUserMedia/getDisplayMedia error. */
export function mediaErrorMessage(err: unknown): string {
  const name = (err as { name?: string } | null)?.name ?? "";
  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return "Your browser blocked the microphone or camera. Allow it in the address bar, then try again.";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "No microphone or camera was found. Plug one in, or join with it switched off.";
    case "NotReadableError":
    case "TrackStartError":
      return "Another app is using your microphone or camera. Close it, then try again.";
    case "OverconstrainedError":
      return "Your microphone or camera doesn't support the settings needed. Try a different one.";
    case "SecurityError":
      return "Your browser only allows the microphone and camera on a secure (https) page.";
    case "AbortError":
      return "The microphone or camera stopped unexpectedly. Try again.";
    default:
      return "Preekie couldn't start your microphone or camera. Check your browser's settings, then try again.";
  }
}

/** CAP-009 (BR-0031): the person who RECORDED in place of "Host" -- by the server's is_host flag, never by matching
 * text -- in the new console only; "Host" when the name isn't known. */
export function speakerLabel(raw: string | null, isHost: boolean | undefined, hostName: string | null | undefined, enabled: boolean): string {
  if (enabled && isHost) return hostName?.trim() || "Host";
  return raw ?? "";
}

/** CAP-009 (A s.21; D's condition 3): a recording's pause as the notes and transcript show it -- "[paused 10:31 am–10:40 am]",
 * "[paused from 10:31 am]" while still open. The new console only. */
export function pauseMarker(from: string, to: string | null, timeZone?: string): string {
  return to ? `[paused ${formatTime(from, timeZone)}–${formatTime(to, timeZone)}]` : `[paused from ${formatTime(from, timeZone)}]`;
}

/** "The recording was paused 10:31 am–10:40 am and 11:02 am–11:05 am." for the notes page; null when it never was. */
export function pausesLine(pauses: ReadonlyArray<{ from: string; to: string | null }> | undefined, timeZone?: string): string | null {
  if (!pauses?.length) return null;
  const parts = pauses.map((p) => (p.to ? `${formatTime(p.from, timeZone)}–${formatTime(p.to, timeZone)}` : `from ${formatTime(p.from, timeZone)}`));
  const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  return `The recording was paused ${list}. Nothing was recorded then.`;
}
